import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useSegmentationTaskProcessing } from '~/backend/connector-api/connector-components';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { Job } from '~/state/atoms/jobAtoms';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { autoSegmentationTaskState } from '~/state/atoms/structureSet/autoSegmentationTaskAtom';
import { reviewToolState } from '~/state/atoms/toolsAtoms';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { transformTaskToGenerationModel } from '~/utils/transforms/taskTransforms';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useStructureSetCacheUpdate from '../useStructureSetCacheUpdate';

type UseAutoSegmentationTaskProps = {
    studyId: string;
    seriesId: string;
    patientId?: string;
    structureSetSopInstanceUid?: string;
};

/**
 * A hook for creating an auto-segmentation task via Connector API.
 * @param {UseAutoSegmentationTaskProps} props - Contains study and series identifiers for the task.
 * @returns {Object} - An object containing the start function and submission state.
 */
export default function useAutoSegmentationTask({
    studyId,
    seriesId,
    patientId,
    structureSetSopInstanceUid,
}: UseAutoSegmentationTaskProps) {
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const autoSegmentationTaskOptions = useRecoilValue(
        autoSegmentationTaskState
    );
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const setReviewTool = useSetRecoilState(reviewToolState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { mutateAsync: startSegmentation } = useSegmentationTaskProcessing();
    const updateCachedStructureSet = useStructureSetCacheUpdate();
    const { addJob, updateJob } = useJobManagement();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { setEnvironmentStructureSet } = useViewportEnvironment();

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            const generationModel = transformTaskToGenerationModel(job.task);
            const processedItems = await processCreatedFiles(
                job.results,
                generationModel
            );
            if (processedItems) {
                updateJob(job.id, { isImported: true });
                setEnvironmentStructureSet(
                    processedItems.structureSets,
                    selectedPatientId ?? ''
                );
                setReviewTool(true);
            }
        }
    };

    /**
     * Creates the auto-segmentation task with the provided model.
     * @param {PredictionModel} selectedModel - The model to use for the segmentation task.
     */
    const start = async (
        selectedModel: PredictionModel,
        selectedStructures: string[],
        customizationName?: string
    ) => {
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        try {
            if (autoSegmentationTaskOptions?.includeStructureSet) {
                await updateCachedStructureSet();
            }

            const modelIdentifier = customizationName
                ? `${selectedModel.name}.${customizationName}`
                : selectedModel.name;

            const task = await startSegmentation({
                body: {
                    modelName: modelIdentifier,
                    imageSeries: {
                        seriesInstanceUID: seriesId,
                        studyInstanceUID: studyId,
                    },
                    ...(selectedStructures?.length && {
                        rois: selectedStructures,
                    }),
                    ...(structureSetSopInstanceUid &&
                        patientId &&
                        autoSegmentationTaskOptions?.includeStructureSet && {
                            structureSet: {
                                sopInstanceUID: structureSetSopInstanceUid,
                                patientID: patientId,
                            },
                        }),
                },
            });

            const newJobDetails: NewJobData = {
                id: task.id,
                name: 'Contour+',
                type: WorkflowTypes.CONTOUR,
                onLoad: handleProcessedFiles,
                task: {
                    model: selectedModel,
                    subSelectedStructures: selectedStructures,
                    output: task,
                    includeStructureSet:
                        autoSegmentationTaskOptions?.includeStructureSet,
                },
            };

            addJob(newJobDetails);
            setWorkflowModalState((state) => ({
                ...state,
                open: false,
            }));
        } catch (error) {
            handleError(error, 'Failed to start auto-contouring task.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return { start, isSubmitting };
}
